/**
 * tiers.ts — Feature-Matrix Free / Premium / Ultra für die Abo-Vergleichstabelle (M-UX-G3).
 *
 * Reine Daten-/Konfigurationsdatei (keine JSX). Wird von `SubscriptionPage` hinter
 * `REACT_APP_ULTRA_TIER_ENABLED` konsumiert (fail-closed, Default aus — Flag off
 * zeigt die heutige 2-Kachel-Ansicht byte-identisch, siehe SubscriptionPage.tsx).
 *
 * ⛔ cfo: Live-Preis-IDs für Ultra werden bewusst NICHT hier hartkodiert (weder
 * Test- noch Live-IDs) — siehe architecture-ux-restructure.md §9/§12. Der
 * Checkout-Button für Ultra bleibt strukturell vorhanden, aber deaktiviert
 * (`isUltraCheckoutReady()` === false), bis beide ENV-Preis-IDs gesetzt sind.
 * ⛔ jurist: Preisdarstellung (PAngV) und Feature-Claims der Tabelle müssen vor
 * dem Flag-Flip in Produktion geprüft werden (architecture §12 Gate-Tabelle).
 *
 * VERIFY (architecture §9, im Rahmen von M-UX-G3 geprüft): `createCheckoutSession`
 * (functions/src/index.ts) validiert `priceId` nur auf Vorhandensein, keine
 * Allowlist-Prüfung gegen bekannte Preis-IDs — ein separates functions-Delta ist
 * für die Ultra-Freischaltung NICHT nötig, sobald die Preis-IDs feststehen.
 *
 * @module config/tiers
 */

/** Die drei Abo-Stufen der Vergleichstabelle. */
export type TierId = 'free' | 'premium' | 'ultra';

/** Stabile Reihenfolge der Tiers für Iteration in der UI. */
export const TIER_IDS: readonly TierId[] = ['free', 'premium', 'ultra'];

/**
 * Eine Zeile der Vergleichstabelle: Übersetzungs-Key + je Tier, ob das Merkmal
 * enthalten ist. Label liegt unter `subscription.json` → `tierTable.features.<labelKey>`.
 */
export interface TierFeatureRow {
  labelKey: string;
  included: Record<TierId, boolean>;
  /**
   * M-U5 (Ultra-GA Welle 1): markiert ein Ultra-Feature als „in Kürze" — es steht
   * als Verkaufsargument in der Ultra-Spalte, ist aber noch nicht ausgeliefert
   * (Flag bewusst aus). `undefined`/`false` ⇒ heutiges Verhalten (additiv,
   * fail-closed). Wird in `SubscriptionPage`-`FeatureRow` als Badge gerendert.
   */
  comingSoon?: boolean;
}

/**
 * Merkmals-Matrix der Vergleichstabelle (§3 ux-audit-growth.md: 3-Tier-Logik).
 * Ultra-exklusive Zeilen (`bankReconciliation`, `receiptScanner`, `datevExport`,
 * `ustva`, `travelExpenses`, `vorsteuerCockpit`) sind die vom Growth-Audit als
 * unsichtbar identifizierten Werttreiber — ab jetzt sichtbar, unabhängig vom
 * Kauf-Status (Nordstern: ein unsichtbarer Tier verkauft sich nicht).
 *
 * INVARIANTE: jede Zeile, deren `canUseX`-Gate `tier === 'ultra'` verlangt,
 * gehört hier hinein. `travelExpenses`/`vorsteuerCockpit` fehlten bis
 * 2026-07-29 — die Tabelle verkaufte Ultra ohne zwei seiner Kaufargumente.
 */
export const TIER_FEATURE_ROWS: readonly TierFeatureRow[] = [
  {
    labelKey: 'invoicing',
    included: { free: true, premium: true, ultra: true },
  },
  {
    labelKey: 'customerProductManagement',
    included: { free: true, premium: true, ultra: true },
  },
  {
    labelKey: 'unlimitedInvoices',
    included: { free: false, premium: true, ultra: true },
  },
  {
    // Entscheidung 3 (architecture-pricing-repackaging.md §0/§3.7): KI-Text-
    // verbesserung bleibt FREE — kein Server-Gate, nur diese Matrix-Zeile.
    labelKey: 'aiEnhancement',
    included: { free: true, premium: true, ultra: true },
  },
  {
    labelKey: 'bankReconciliation',
    included: { free: false, premium: false, ultra: true },
  },
  {
    // D-2 (Re-Loop 1, 2026-07-29): `comingSoon: true` ENTFERNT. Die Begründung
    // von M-U5 („REACT_APP_RECEIPT_SCAN_ENABLED bleibt aus") ist überholt —
    // `.env.production:201` setzt das Flag auf `true` (GA 2026-07-28, der
    // frühere jurist-VETO wurde mit AVV + TIA aufgehoben). Die Tabelle
    // brandmarkte damit ein LIVE nutzbares Feature als „In Kürze", während der
    // Belegscanner-Teaser auf derselben App zum Kauf aufforderte. Kein
    // §5-UWG-Verstoß (Untertreibung wirkt nicht zulasten des Kunden), aber
    // sachlich falsch und ein untergrabener Kaufanreiz.
    labelKey: 'receiptScanner',
    included: { free: false, premium: false, ultra: true },
  },
  {
    // Ultra-Differenzierer (§1/§3.2): DATEV-Buchungsstapel + Belege-Paket
    // (Owner-Pfad). Kanzlei-/Advisor-Nutzung ist ein eigener Track (§1.2/§3.2b).
    labelKey: 'datevExport',
    included: { free: false, premium: false, ultra: true },
  },
  {
    // Ultra-Differenzierer (§1, CFO-Empfehlung): USt-Voranmeldungs-Vorschau.
    // GA-Freischaltung 2026-07-26 (modules-ga-freischaltung.md §2 #10): REACT_APP_USTVA_ENABLED
    // wird scharf → nicht mehr „in Kürze", sondern verfügbar (comingSoon entfernt).
    labelKey: 'ustva',
    included: { free: false, premium: false, ultra: true },
  },
  {
    // Ultra-Differenzierer (Ultra-Upsell-Konsistenz 2026-07-29): Reisekosten-/
    // Dienstreisen-Erfassung. Fehlte bisher komplett in der Vergleichstabelle,
    // obwohl das Gate (`travelExpensesAccess.ts`) Ultra verlangt und
    // REACT_APP_TRAVEL_EXPENSES_ENABLED live scharf ist — ein Feature, das
    // Ultra verkaufen soll, stand nicht im Angebot. KEIN `comingSoon` (live).
    labelKey: 'travelExpenses',
    included: { free: false, premium: false, ultra: true },
  },
  {
    // Ultra-Differenzierer (Ultra-Upsell-Konsistenz 2026-07-29): Vorsteuer-
    // Cockpit. Analog `travelExpenses` — Gate (`vorsteuerCockpitAccess.ts`)
    // verlangt Ultra, REACT_APP_VORSTEUER_COCKPIT_ENABLED ist live scharf, die
    // Zeile fehlte aber in der Tabelle. KEIN `comingSoon` (live).
    labelKey: 'vorsteuerCockpit',
    included: { free: false, premium: false, ultra: true },
  },
];

/** Tier, das in der Tabelle als „empfohlen" hervorgehoben wird. */
export const RECOMMENDED_TIER: TierId = 'premium';

/**
 * Ultra-Preis-IDs für den Stripe-Checkout. Bewusst `undefined`, solange das
 * cfo-Gate nicht gezogen wurde — NIE hier hartkodieren (auch keine Test-IDs).
 * ⛔ cfo: Nach Freigabe live über die ENV-Variablen setzen, kein Code-Delta nötig.
 */
export const ULTRA_PRICE_ID_MONTHLY: string | undefined = process.env.REACT_APP_ULTRA_PRICE_ID_MONTHLY;
export const ULTRA_PRICE_ID_YEARLY: string | undefined = process.env.REACT_APP_ULTRA_PRICE_ID_YEARLY;

/**
 * Sichtbarkeits-Flag der 3-Spalten-Tabelle. Fail-closed: nur bei explizitem
 * `'true'` aktiv. Modul-Konstante, einmalig beim Bundle-Load ausgewertet
 * (Muster: `receiptScanAccess.ts`, `App.tsx`).
 */
export const ULTRA_TIER_FLAG: boolean = process.env.REACT_APP_ULTRA_TIER_ENABLED === 'true';

/**
 * Fail-closed-Gate für den Ultra-Checkout-Button — unabhängig vom Sichtbarkeits-Flag.
 * Die Tabelle kann Ultra bereits zeigen (Flag on), ohne dass gekauft werden kann:
 * Sichtbarkeit (Marketing, jurist-Gate) und Kaufbarkeit (Preis-IDs, cfo-Gate) sind
 * bewusst zwei getrennte Freischalt-Schritte.
 *
 * @returns `true` nur wenn das Flag aktiv ist UND beide Preis-IDs gesetzt sind.
 */
export function isUltraCheckoutReady(): boolean {
  return ULTRA_TIER_FLAG && Boolean(ULTRA_PRICE_ID_MONTHLY) && Boolean(ULTRA_PRICE_ID_YEARLY);
}
